/* eslint-disable camelcase */
import { attributionLabelSources, labelSourceRules } from '@/api/attributionLabelSources'
import type {
  AttributionLabelRule,
  AttributionLabelSource,
  AttributionSourceSelection,
  AttributionSummary,
} from '@/api/attributionObservability'

export const labelSourceOrder = attributionLabelSources
export const labelSourceLabels: Record<AttributionLabelSource, string> = {
  ax: 'AX',
  channel: 'Channel',
  llm: 'LLM',
  unnamed: 'Unnamed',
}
export const channelRules = labelSourceRules.channel
export const llmRoutes = labelSourceRules.llm
export const labelRuleLabels: Record<AttributionLabelRule, string> = {
  dom: 'AX match',
  dom_contested: 'Contested AX match',
  rescue: 'Rescued AX match',
  mic_content_owner: 'Mic content → owner',
  mic_sole_speaker_owner: 'Sole mic speaker → owner',
  system_content_attendee: 'System content → attendee',
  paired_sole_attendee: 'Sole attendee → system',
  roster_calendar: 'Roster match · calendar person',
  roster_other: 'Roster match · other person',
  naming_link_calendar: 'Name link · calendar person',
  naming_link_other: 'Name link · other person',
  naming_created: 'New person',
  prior: 'Prior assignment',
  unknown: 'Unknown',
}

export const validLabelSources = (summary: AttributionSummary) => {
  const data = summary.label_sources
  if (
    !data ||
    ![data.measured_meetings, data.unmeasured_meetings, data.active_speakers].every(isCount) ||
    data.measured_meetings + data.unmeasured_meetings !== summary.meeting_count ||
    data.sources.length !== labelSourceOrder.length ||
    new Set(data.sources.map((item) => item.source)).size !== labelSourceOrder.length ||
    data.sources.reduce((total, item) => total + item.speakers, 0) !== data.active_speakers
  )
    return null
  for (const source of labelSourceOrder) {
    const item = data.sources.find((entry) => entry.source === source)
    if (!item || ![item.speakers, item.meetings].every(isCount) || item.meetings > data.measured_meetings) return null
    const allowed: readonly string[] = labelSourceRules[source]
    // A backend older than this vocabulary omits newer rules; the speaker-sum check still enforces a full partition.
    if (
      new Set(item.rules.map((rule) => rule.rule)).size !== item.rules.length ||
      item.rules.some(
        (rule) =>
          !allowed.includes(rule.rule) ||
          ![rule.speakers, rule.meetings].every(isCount) ||
          rule.meetings > item.meetings
      ) ||
      (allowed.length > 0 && item.rules.reduce((total, rule) => total + rule.speakers, 0) !== item.speakers)
    )
      return null
  }
  return data
}

export const selectedLabelSource = (summary: AttributionSummary, selection: AttributionSourceSelection) => {
  const data = validLabelSources(summary)
  const source = data?.sources.find((item) => item.source === selection.label_source)
  if (!data || !source) return null
  const selected = selection.label_rule ? source.rules.find((item) => item.rule === selection.label_rule) : source
  return selected
    ? {
        ...selected,
        denominator: selection.label_rule ? source.speakers : data.active_speakers,
        measured: data.measured_meetings,
      }
    : null
}

export const validLabelEvaluation = (summary: AttributionSummary, source: AttributionLabelSource) => {
  if (source === 'unnamed') return null
  const matches = summary.label_sources?.sources.filter((item) => item.source === source)
  if (matches?.length !== 1) return null
  const evaluation = matches[0].evaluation
  if (
    !evaluation ||
    ![
      evaluation.evaluated_speakers,
      evaluation.claimed_speakers,
      evaluation.measured_meetings,
      evaluation.evaluated_meetings,
    ].every(isCount) ||
    evaluation.claimed_speakers > evaluation.evaluated_speakers ||
    evaluation.measured_meetings > summary.meeting_count ||
    evaluation.evaluated_meetings > evaluation.measured_meetings ||
    evaluation.evaluated_meetings > evaluation.evaluated_speakers ||
    (evaluation.evaluated_meetings === 0) !== (evaluation.evaluated_speakers === 0)
  )
    return null
  return evaluation
}

const isCount = (value: number) => Number.isSafeInteger(value) && value >= 0
